import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Modal, EmptyState } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import crmAPI from '../api/crmAPI';
import toast from 'react-hot-toast';

const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: '#fff', fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: 'var(--font-body)' };
const labelStyle = { fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '5px' };
const cardStyle = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' };

const EMPTY = {
  name: '', category: '', product_type: 'product', sku: '',
  short_description: '', full_description: '', sale_context: '',
  currency: 'COP', price: '', discount_percent: 0, price_notes: '',
  show_price: true, delivery_time: '', warranty: '',
  target_audience: '', competitive_advantage: '',
  ai_pitch: '', ai_keywords: '', ai_faq: '', ai_objections: '',
  sale_type: 'consultive', payment_link: '', brochure_url: '', image_url: '',
  implementation_price: '', implementation_price_notes: '', monthly_price_saas: '', monthly_price_onpremise: '', monthly_price_notes: '', hosting_options: 'both', support_includes: '',
  features: '', payment_methods: '', is_active: true, is_featured: false, order_index: 0,
  promo_enabled: false, promo_text: '', promo_valid_until: '',
};

const TYPE_LABEL = { product: '📦 Producto', service: '🛠 Servicio', both: '📦🛠 Ambos' };
const TYPE_COLOR = { product: '#3b82f6', service: '#10b981', both: '#f59e0b' };

function Field({ label, children }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

function ProductForm({ initial, onSave, onCancel, loading }) {
  const [form, setForm] = useState(initial ? { ...EMPTY, ...initial, sale_type: initial.sale_type || 'consultive', payment_link: initial.payment_link || '', brochure_url: initial.brochure_url || '', image_url: initial.image_url || '', hosting_options: initial.hosting_options || 'both', support_includes: Array.isArray(initial.support_includes) ? initial.support_includes.join(', ') : (initial.support_includes || '') } : { ...EMPTY });
  const [tab, setTab] = useState('basic');
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target ? e.target.value : e }));

  const tabs = [
    { id: 'basic', label: '📋 Básico' },
    { id: 'pricing', label: '💰 Precios' },
    { id: 'details', label: '📝 Detalles' },
    { id: 'ai', label: '🤖 Agente IA' },
  ];

  const handleSave = () => {
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return; }
    const payload = { ...form };
    // Convertir campos de texto separados por coma a arrays
    ['ai_keywords', 'payment_methods'].forEach(k => {
      if (typeof payload[k] === 'string' && payload[k]) {
        payload[k] = payload[k].split(',').map(s => s.trim()).filter(Boolean);
      } else if (!payload[k]) payload[k] = null;
    });
    // features: una por linea (los textos suelen tener comas internas)
    if (typeof payload.features === 'string' && payload.features) {
      payload.features = payload.features.split('\n').map(s => s.trim()).filter(Boolean);
    } else if (!payload.features) payload.features = null;
    // Convertir ai_faq y ai_objections de texto JSON a array
    ['ai_faq', 'ai_objections'].forEach(k => {
      try { if (typeof payload[k] === 'string' && payload[k]) payload[k] = JSON.parse(payload[k]); }
      catch { payload[k] = null; }
    });
    if (payload.price === '') payload.price = null;
    if (payload.discount_percent === '') payload.discount_percent = 0;
    onSave(payload);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-elevated)', padding: '4px', borderRadius: '10px', border: '1px solid var(--border)' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, padding: '7px', borderRadius: '7px', border: 'none', background: tab === t.id ? 'var(--bg-card)' : 'transparent', color: tab === t.id ? '#fff' : 'var(--text-muted)', fontSize: '12px', fontWeight: tab === t.id ? '700' : '500', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'basic' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Field label="Nombre *">
            <input style={inputStyle} value={form.name} onChange={set('name')} placeholder="Ej: Cámara IP 4K" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Categoría">
              <input style={inputStyle} value={form.category} onChange={set('category')} placeholder="Ej: Seguridad" />
            </Field>
            <Field label="Tipo">
              <select style={inputStyle} value={form.product_type} onChange={set('product_type')}>
                <option value="product">Producto</option>
                <option value="service">Servicio</option>
                <option value="both">Ambos</option>
              </select>
            </Field>
          </div>
          <Field label="Descripción corta">
            <input style={inputStyle} value={form.short_description} onChange={set('short_description')} placeholder="Resumen en una línea" />
          </Field>
          <Field label="Descripción completa">
            <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} value={form.full_description} onChange={set('full_description')} placeholder="Descripción detallada del producto/servicio" />
          </Field>
          <Field label="Características (una por línea)">
            <textarea
              style={{ ...inputStyle, minHeight: '110px', resize: 'vertical', fontFamily: 'inherit' }}
              value={typeof form.features === 'object' && form.features ? form.features.join('\n') : form.features}
              onChange={set('features')}
              placeholder={'Ej:\nMide el ROI del desarrollo humano con datos objetivos\nInformes automáticos y métricas de evolución\nConfidencialidad total de las conversaciones'}
            />
          </Field>
          <Field label="🎯 Ícono del producto">
            <input style={inputStyle} value={form.sku} onChange={set('sku')} placeholder="Ej: 🚀 🤖 📊 🛡️ — emoji que representa el producto" />
          </Field>
          <div style={{ display: 'flex', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              Activo
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
              <input type="checkbox" checked={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))} />
              Destacado
            </label>
          </div>
        </div>
      )}

      {tab === 'pricing' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <Field label="Moneda">
              <select style={inputStyle} value={form.currency} onChange={set('currency')}>
                <option value="COP">COP</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="MXN">MXN</option>
              </select>
            </Field>
            <Field label="Precio">
              <input style={inputStyle} type="number" value={form.price} onChange={set('price')} placeholder="0" />
            </Field>
            <Field label="Descuento %">
              <input style={inputStyle} type="number" value={form.discount_percent} onChange={set('discount_percent')} placeholder="0" min="0" max="100" />
            </Field>
          </div>
          <Field label="Nota de precio">
            <textarea style={{...inputStyle, minHeight: '70px', resize: 'vertical'}} value={form.price_notes} onChange={set('price_notes')} placeholder="Ej: Precio desde, IVA incluido, Consultar" />
          </Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
            <input type="checkbox" checked={form.promo_enabled} onChange={e => setForm(f => ({ ...f, promo_enabled: e.target.checked }))} />
            🎉 Promoción activa
          </label>
          {form.promo_enabled && (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
              <Field label="Texto de la promoción">
                <input style={inputStyle} value={form.promo_text || ''} onChange={set('promo_text')} placeholder="Ej: Oferta de lanzamiento, Gratis por 30 días, 2x1" />
              </Field>
              <Field label="Válida hasta">
                <input style={inputStyle} type="date" value={form.promo_valid_until || ''} onChange={set('promo_valid_until')} />
              </Field>
            </div>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
            <input type="checkbox" checked={form.show_price} onChange={e => setForm(f => ({ ...f, show_price: e.target.checked }))} />
            Mostrar precio al cliente
          </label>
          <Field label="Métodos de pago (separados por coma)">
            <input style={inputStyle} value={typeof form.payment_methods === 'object' && form.payment_methods ? form.payment_methods.join(', ') : form.payment_methods} onChange={set('payment_methods')} placeholder="Efectivo, Transferencia, Tarjeta, Crédito" />
          </Field>
          <Field label="Tiempo de entrega">
            <input style={inputStyle} value={form.delivery_time} onChange={set('delivery_time')} placeholder="Ej: 3-5 días hábiles" />
          </Field>
          <Field label="Garantía">
            <input style={inputStyle} value={form.warranty} onChange={set('warranty')} placeholder="Ej: 1 año de garantía del fabricante" />
          </Field>
          <Field label="Tipo de venta">
            <select style={inputStyle} value={form.sale_type || 'consultive'} onChange={set('sale_type')}>
              <option value="consultive">🔧 Implementación</option>
              <option value="direct">🌐 Online — Compra directa + acceso inmediato</option>
              <option value="hub">🖥️ Hub / Mini servidor — Configuración básica</option>
            </select>
          </Field>
          <Field label="Link de pago / planes">
            <input style={inputStyle} value={form.payment_link || ''} onChange={set('payment_link')} placeholder="https://..." />

          {/* ── SECCIÓN PRECIOS ── */}
          <div style={{ gridColumn: '1/-1', marginTop: '8px', padding: '16px', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)' }}>
            <p style={{ fontSize: '13px', fontWeight: '700', marginBottom: '14px', color: 'var(--text-primary)' }}>💰 Estructura de Precios</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Implementación — precio único (COP)</label>
                <input type="number" style={inputStyle} value={form.implementation_price || ''} onChange={set('implementation_price')} placeholder="Ej: 4500000" />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Nota implementación (si el precio varía)</label>
                <textarea style={{...inputStyle, minHeight: '70px', resize: 'vertical'}} value={form.implementation_price_notes || ''} onChange={set('implementation_price_notes')} placeholder="Ej: Sujeto a validación de infraestructura" />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Mensualidad SaaS — hosting con nosotros (COP/mes)</label>
                <input type="number" style={inputStyle} value={form.monthly_price_saas || ''} onChange={set('monthly_price_saas')} placeholder="Ej: 350000" />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Mensualidad On-Premise — servidor del cliente (COP/mes)</label>
                <input type="number" style={inputStyle} value={form.monthly_price_onpremise || ''} onChange={set('monthly_price_onpremise')} placeholder="Ej: 200000" />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Nota mensualidad</label>
                <textarea style={{...inputStyle, minHeight: '70px', resize: 'vertical'}} value={form.monthly_price_notes || ''} onChange={set('monthly_price_notes')} placeholder="Ej: Sujeto a validación técnica de infraestructura del cliente" />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Modalidad de hosting</label>
                <select style={inputStyle} value={form.hosting_options || 'both'} onChange={set('hosting_options')}>
                  <option value="both">Ambas (SaaS y On-Premise)</option>
                  <option value="saas">Solo SaaS (hosting con nosotros)</option>
                  <option value="onpremise">Solo On-Premise (servidor del cliente)</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Póliza incluye (separado por coma)</label>
                <input style={inputStyle} value={form.support_includes || ''} onChange={set('support_includes')} placeholder="Soporte técnico, Actualizaciones, Hosting, SLA 99.9%" />
              </div>
            </div>
          </div>
          </Field>
          <Field label="URL Imagen del producto">
            <input style={inputStyle} value={form.image_url || ''} onChange={set('image_url')} placeholder="https://imagen.com/producto.jpg" />
          </Field>
          <Field label="Brochure / Manual (URL o link de descarga)">
            <input style={inputStyle} value={form.brochure_url || ''} onChange={set('brochure_url')} placeholder="https://drive.google.com/..." />
          </Field>
        </div>
      )}

      {tab === 'details' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Field label="Público objetivo">
            <input style={inputStyle} value={form.target_audience} onChange={set('target_audience')} placeholder="Ej: Empresas de seguridad, hogares, pymes" />
          </Field>
          <Field label="Ventaja competitiva">
            <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} value={form.competitive_advantage} onChange={set('competitive_advantage')} placeholder="¿Por qué elegir este producto sobre la competencia?" />
          </Field>
          <Field label="Contexto completo para el agente IA">
            <textarea style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }} value={form.sale_context} onChange={set('sale_context')} placeholder="Escribe aquí toda la información que el agente debe conocer y usar al hablar de este producto: casos de uso, instalación, requisitos, ejemplos, etc." />
          </Field>
        </div>
      )}

      {tab === 'ai' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Field label="Palabras clave (separadas por coma)">
            <input style={inputStyle} value={typeof form.ai_keywords === 'object' && form.ai_keywords ? form.ai_keywords.join(', ') : form.ai_keywords} onChange={set('ai_keywords')} placeholder="Ej: cámara, seguridad, cctv, vigilancia" />
          </Field>
          <Field label="Pitch de venta del agente">
            <textarea style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }} value={form.ai_pitch} onChange={set('ai_pitch')} placeholder="Texto que el agente usará para vender este producto. Ej: Nuestra cámara 4K es la solución perfecta para..." />
          </Field>
          <Field label='Preguntas frecuentes (JSON: [{"q":"...","a":"..."}])'>
            <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical', fontFamily: 'monospace', fontSize: '11px' }} value={typeof form.ai_faq === 'object' && form.ai_faq ? JSON.stringify(form.ai_faq, null, 2) : form.ai_faq} onChange={set('ai_faq')} placeholder='[{"q":"¿Tiene garantía?","a":"Sí, 1 año"}]' />
          </Field>
          <Field label='Manejo de objeciones (JSON: [{"objection":"...","response":"..."}])'>
            <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical', fontFamily: 'monospace', fontSize: '11px' }} value={typeof form.ai_objections === 'object' && form.ai_objections ? JSON.stringify(form.ai_objections, null, 2) : form.ai_objections} onChange={set('ai_objections')} placeholder='[{"objection":"Es muy caro","response":"Considera que..."}]' />
          </Field>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '8px' }}>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancelar</Button>
        <Button onClick={handleSave} loading={loading}>{initial ? 'Guardar cambios' : 'Crear producto'}</Button>
      </div>
    </div>
  );
}

export default function ProductsPage() {
  const { isAdmin, isCrmAdmin } = useAuth();
  const canManageProducts = isAdmin || isCrmAdmin;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [noTenant, setNoTenant] = useState(false);

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    setLoading(true);
    setNoTenant(false);
    try {
      const { data } = await crmAPI.listProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      if (e?.response?.status === 404 && detail.toLowerCase().includes('no asociado a ninguna empresa')) {
        setNoTenant(true);
      } else {
        toast.error(detail || 'Error cargando productos');
      }
    }
    finally { setLoading(false); }
  };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editing) {
        await crmAPI.updateProduct(editing.id, form);
        toast.success('Producto actualizado ✓');
      } else {
        await crmAPI.createProduct(form);
        toast.success('Producto creado ✓');
      }
      await fetchProducts();
      setShowForm(false);
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      await crmAPI.deleteProduct(id);
      toast.success('Producto eliminado');
      await fetchProducts();
    } catch { toast.error('Error al eliminar'); }
  };

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.category || '').toLowerCase().includes(search.toLowerCase())
  );

  const finalPrice = (p) => {
    if (!p.price) return null;
    const disc = parseFloat(p.discount_percent || 0);
    return disc > 0 ? p.price * (1 - disc / 100) : p.price;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <input
          style={{ ...inputStyle, maxWidth: '280px' }}
          placeholder="🔍 Buscar producto..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {canManageProducts && (
          <Button icon="+" onClick={() => { setEditing(null); setShowForm(true); }}>Nuevo producto</Button>
        )}
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Cargando productos...</p>
      ) : noTenant ? (
        <EmptyState icon="🏢" title="No perteneces a ninguna empresa activa" description="Tu usuario no está asignado a ninguna empresa en este momento. Contacta a un administrador de tu equipo para que te vuelva a agregar." />
      ) : filtered.length === 0 ? (
        <EmptyState icon="📦" title="Sin productos" description="Agrega productos o servicios para que el agente IA los conozca y pueda venderlos." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '14px' }}>
          {filtered.map((p, i) => {
            const fp = finalPrice(p);
            return (
              <Card key={p.id} style={{ padding: '16px', opacity: p.is_active ? 1 : 0.5 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                      <span style={{ fontWeight: '700', fontSize: '14px' }}>{p.name}</span>
                      {p.is_featured && <Badge color="#f59e0b">⭐ Destacado</Badge>}
                      {!p.is_active && <Badge color="#ef4444">Inactivo</Badge>}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <Badge color={TYPE_COLOR[p.product_type] || '#06b6d4'}>{TYPE_LABEL[p.product_type] || p.product_type}</Badge>
                      {p.category && <Badge color="#6b7280">{p.category}</Badge>}
                    </div>
                  </div>
                  {canManageProducts && (
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginLeft: '8px' }}>
                      <button onClick={() => { setEditing(p); setShowForm(true); }} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-muted)', cursor: 'pointer', padding: '5px 8px', borderRadius: '7px', fontSize: '12px' }}>✏️</button>
                      <button onClick={() => handleDelete(p.id)} style={{ background: '#ef444415', border: '1px solid #ef444430', color: '#ef4444', cursor: 'pointer', padding: '5px 8px', borderRadius: '7px', fontSize: '12px' }}>🗑</button>
                    </div>
                  )}
                </div>

                {p.short_description && (
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '10px', lineHeight: '1.5' }}>{p.short_description}</p>
                )}

                {(p.price || p.price_notes) && (
                  <div style={{ padding: '8px 12px', background: 'var(--bg-elevated)', borderRadius: '8px', marginBottom: '10px' }}>
                    {p.show_price && fp ? (
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                        <span style={{ fontWeight: '800', fontSize: '16px', color: '#10b981' }}>
                          {p.currency} {parseFloat(fp).toLocaleString('es-CO')}
                        </span>
                        {parseFloat(p.discount_percent) > 0 && (
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)', textDecoration: 'line-through' }}>
                            {p.currency} {parseFloat(p.price).toLocaleString('es-CO')}
                          </span>
                        )}
                        {parseFloat(p.discount_percent) > 0 && (
                          <Badge color="#ef4444">-{p.discount_percent}%</Badge>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{p.price_notes || 'Precio a consultar'}</span>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'var(--text-muted)' }}>
                  {p.delivery_time && <span>🚚 {p.delivery_time}</span>}
                  {p.warranty && <span>🛡 {p.warranty}</span>}
                </div>

                {p.ai_keywords?.length > 0 && (
                  <div style={{ marginTop: '10px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {p.ai_keywords.slice(0, 5).map((kw, j) => (
                      <span key={j} style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '20px', background: '#7c3aed20', color: '#a78bfa', border: '1px solid #7c3aed30' }}>{kw}</span>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        open={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        title={editing ? `Editar: ${editing.name}` : 'Nuevo producto / servicio'}
        width={600}
      >
        <ProductForm
          initial={editing}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          loading={saving}
        />
      </Modal>
    </div>
  );
}
